import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  LIMITE_DE_CAMADAS, LIMITE_DE_VERTICAIS_NO_LOTE, codigoDaCampanha, gravarCamadas, gravarCamadasDaCampanha, type CampanhaCompleta, type GravacaoDasCamadas, type OrigemDaCamada,
  type VerticalCompleta,
} from '../../api/cptu';
import {
  GRANDEZAS, GRANDEZAS_DA_ESTATISTICA, TRATIO_INICIAL, TRATIO_INICIAL_QT, comUnidadePelaZona, estatisticaPorCamada,
  sugerirPorAgrupamento, sugerirPorTRatio,
  type Grandeza, type ParametrosDoTRatio, type Resultado, type SugestaoDeCamadas,
} from '../../cptu/motor';
import { marcaDaColuna, numeroBr, textoDoValor, type EscalaDoGrafico, type TrechoDoZoom } from '../../cptu/visual';
import { faixaProfundidade, numero, paraCampo, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { Area, Aviso, Carregando, CampoNumerico, Dialogo, ErroDaApi } from '../../ui';
import { useAnaliseDaCampanha } from './analise';
import { AcoesBloqueadas, motivoDoBloqueio } from './CampanhaMoldura';
import { focarOPrimeiro, useFocoDeVolta } from './interpretacao';
import { FormularioDaJanela } from './FormularioDaJanela';
import {
  FAIXAS_DOS_PARAMETROS_DO_TRATIO, gravarParametrosDoTRatio, parametrosDoTRatioGuardados,
} from './parametrosDasCamadas';
import { NotasDoPorque } from './TabelaDaVertical';
import { TrilhasDaSugestao, grandezaDaTrilha, mediasDasCamadas } from './TrilhasDaSugestao';

/**
 * As camadas da vertical — onda 313, Tarefa 313.6 (spec 07 §5.6 e §6.1, item 4; R12, R13; RN-253).
 *
 * As camadas que valem no relatório são as que o engenheiro grava (topo, base, unidade, descrição); as do motor
 * (`camadas.ts`) são SUGESTÕES, pedidas por botão e nunca sozinhas (fora do RN-252): o T-ratio sobre o Ic (a janela
 * e o limite à vista, começando nos valores do motor, `TRATIO_INICIAL`, escolhidos sem fonte — a assinar pelo RT) e o
 * agrupamento contíguo sobre log qt e log Fr (pelo maior ganho, ou um número de 2 a 12 grupos). A sugestão desenha as
 * fronteiras tracejadas nas trilhas; «Aceitar a sugestão» troca o rascunho pelas camadas C1…Cn, com a origem e os
 * parâmetros dela.
 *
 * O rascunho das camadas é uma tabela editável — «Dividir no cursor», «Juntar com a de baixo», «Tirar» (tirar é
 * retirar: na gravação a camada sai das vigentes e fica no histórico, nada se apaga) —, com a sobreposição e a camada
 * invertida apontadas na linha antes de gravar. «Gravar as camadas» manda o conjunto inteiro, com motivo, pelo
 * `PUT /cptu/verticais/:id/camadas`: as vigentes iguais ficam, as que não vieram são retiradas, as novas entram.
 *
 * A estatística por camada (`estatisticaPorCamada`): as leituras de cada camada [topo, base) e, para qt, fs, u₂, Ic,
 * Su, φ', Dr, M e γ, média ± desvio, mínimo–máximo e CV, com a marca do R7 e «não se aplica» onde a camada não tem
 * leitura com valor.
 *
 * Sem `cptu.editar`, ou com a campanha fora de rascunho/devolvida, as camadas e a estatística ficam para ler e as
 * ações à vista, bloqueadas com o motivo (P-UI-24). No celular é só leitura, sem ação nenhuma (R8).
 *
 * Onda 347, Tarefa 10 (spec 2026-10-22 §6, R11): «Camadas automáticas» abre a janela da sugestão — o método (T-ratio
 * sobre o Ic ou log qt, com a espessura mínima e o «juntar vizinhas da mesma zona»; ou o agrupamento contíguo), as
 * trilhas (`TrilhasDaSugestao`), a tabela com a unidade pela zona, «Levar ao rascunho das camadas» e, com «Aplicar a
 * todas as verticais da campanha», «Gravar as camadas de todas as verticais» (o lote, com motivo). As caixas de hoje
 * («Sugerir por T-ratio», «Sugerir por agrupamento») ficam no painel.
 */

/** Uma camada como a tela a edita: o topo e a base como foram digitados (m), e o resto. */
export interface LinhaDaCamada {
  chave: number;
  topo: string;
  base: string;
  unidade: string;
  descricao: string;
  origem: OrigemDaCamada;
  parametros: Record<string, number> | null;
}

/** Uma camada que se lê (topo e base com número, a base abaixo do topo). */
export interface CamadaLida {
  chave: number;
  topo: number;
  base: number;
  unidade: string;
  descricao: string | null;
  origem: OrigemDaCamada;
  parametros: Record<string, number> | null;
}

/**
 * O número de grupos que se pede ao agrupamento contíguo: os do motor (`camadas.ts`, `MINIMO_DE_GRUPOS` e
 * `MAXIMO_DE_GRUPOS`, [cam.agrupamento-grupos]) — um lugar só na tela, para o painel e a janela.
 */
const GRUPOS_PEDIDOS = Object.freeze({ min: 2, max: 12 });
const gruposServem = (g: number | undefined): boolean =>
  g !== undefined && Number.isInteger(g) && g >= GRUPOS_PEDIDOS.min && g <= GRUPOS_PEDIDOS.max;
const PROBLEMA_DOS_GRUPOS = `o número de grupos é um inteiro de ${GRUPOS_PEDIDOS.min} a ${GRUPOS_PEDIDOS.max}`;
const ESCOLHA_DOS_GRUPOS = `um número de grupos, de ${GRUPOS_PEDIDOS.min} a ${GRUPOS_PEDIDOS.max}`;

let chaves = 0;
const novaChave = (): number => { chaves += 1; return chaves; };

/** «11,1» — a profundidade no campo, ao centímetro, sem milhar e sem zeros à direita (`paraCampo`, P-FMT-02). */
const noCampo = (z: number): string => paraCampo(z, 2);
const duasCasas = (z: number): number => Math.round(z * 100) / 100;

/** As camadas vigentes da vertical, como linhas da tabela. */
export function linhasDasVigentes(v: VerticalCompleta): LinhaDaCamada[] {
  return (v.camadas ?? []).map((k) => ({
    chave: novaChave(), topo: noCampo(Number(k.topo_m)), base: noCampo(Number(k.base_m)), unidade: k.unidade,
    descricao: k.descricao ?? '', origem: k.origem, parametros: k.parametros ?? null,
  }));
}

/** As camadas de uma sugestão (C1…Cn), com a origem e os parâmetros dela. */
function linhasDaSugestao(s: SugestaoDeCamadas): LinhaDaCamada[] {
  return s.camadas.map((k) => ({
    chave: novaChave(), topo: noCampo(k.topo), base: noCampo(k.base), unidade: k.unidade, descricao: '',
    origem: k.origem, parametros: { ...s.parametros },
  }));
}

const lerProfundidade = (t: string): number | undefined => {
  const n = paraNumero(t);
  return n !== undefined && Number.isFinite(n) ? n : undefined;
};
const comDuasCasas = (n: number): boolean => Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;
/** «0,01 – 11,10 m» — a faixa como o documento a escreve (`faixaProfundidade`, P-FMT-05). */
const faixa = (topo: number, base: number): string => `${faixaProfundidade(topo, base)} m`;
/** A profundidade de uma linha para ler (a tabela sem campos): com as duas casas, como o documento. */
const paraLer = (t: string): string => {
  const n = lerProfundidade(t);
  return n === undefined ? t : numero(n, 2);
};

/** O que a tabela tem: as camadas que se leem, na ordem do topo, e os problemas de cada linha (pela chave). */
export interface ConferenciaDasCamadas {
  lidas: CamadaLida[];
  problemas: Map<number, string[]>;
  quantosProblemas: number;
}

/**
 * Confere as camadas como a API confere (`esquemaDasCamadas` e o limite da profundidade final): topo e base em metros
 * com duas casas, 0 ou mais, a base abaixo do topo, a unidade com nome, a base até a profundidade final e nenhuma
 * sobreposição — o problema vai na linha, antes de gravar.
 */
export function conferirCamadas(linhas: readonly LinhaDaCamada[], profundidadeFinal: number | null): ConferenciaDasCamadas {
  const problemas = new Map<number, string[]>();
  const anotar = (chave: number, p: string) => problemas.set(chave, [...(problemas.get(chave) ?? []), p]);
  const lidas: CamadaLida[] = [];
  for (const l of linhas) {
    const topo = lerProfundidade(l.topo);
    const base = lerProfundidade(l.base);
    if (topo === undefined) anotar(l.chave, 'o topo não se lê: use vírgula para as casas');
    if (base === undefined) anotar(l.chave, 'a base não se lê: use vírgula para as casas');
    if (topo !== undefined && (topo < 0 || !comDuasCasas(topo))) anotar(l.chave, 'o topo vai em metros, 0 ou mais, com duas casas (o centímetro)');
    if (base !== undefined && (base < 0 || !comDuasCasas(base))) anotar(l.chave, 'a base vai em metros, 0 ou mais, com duas casas (o centímetro)');
    if (topo !== undefined && base !== undefined && !(base > topo)) {
      anotar(l.chave, base === topo ? 'camada sem espessura: a base é igual ao topo'
        : `camada invertida: a base (${numeroBr(base, 2)} m) fica acima do topo (${numeroBr(topo, 2)} m)`);
    }
    if (!l.unidade.trim()) anotar(l.chave, 'dê o nome da unidade (ex.: «U2 · Argila mole»)');
    if (l.unidade.trim().length > 80) anotar(l.chave, 'a unidade tem no máximo 80 caracteres');
    if (l.descricao.trim().length > 500) anotar(l.chave, 'a descrição tem no máximo 500 caracteres');
    if (base !== undefined && profundidadeFinal !== null && base > profundidadeFinal + 0.005) {
      anotar(l.chave, `a base passa da profundidade final da vertical (${numeroBr(profundidadeFinal, 2)} m)`);
    }
    if (topo !== undefined && base !== undefined && base > topo) {
      lidas.push({
        chave: l.chave, topo: duasCasas(topo), base: duasCasas(base), unidade: l.unidade.trim(),
        descricao: l.descricao.trim() || null, origem: l.origem, parametros: l.parametros,
      });
    }
  }
  lidas.sort((a, b) => a.topo - b.topo || a.base - b.base);
  for (let i = 1; i < lidas.length; i++) {
    const cima = lidas[i - 1];
    const esta = lidas[i];
    if (esta.topo < cima.base - 1e-9) {
      anotar(esta.chave, `sobrepõe-se à camada de cima (${cima.unidade || 'sem nome'}, ${faixa(cima.topo, cima.base)}): as camadas não se sobrepõem`);
    }
  }
  let quantosProblemas = 0;
  for (const p of problemas.values()) quantosProblemas += p.length;
  return { lidas, problemas, quantosProblemas };
}

/** O que identifica uma camada gravada (como a API compara): topo, base, unidade, descrição, origem, parâmetros. */
const identidade = (k: { topo: number; base: number; unidade: string; descricao: string | null; origem: string;
  parametros: Record<string, number> | null }): string => JSON.stringify([
  duasCasas(k.topo), duasCasas(k.base), k.unidade.trim(), k.descricao?.trim() || null, k.origem,
  k.parametros && Object.keys(k.parametros).length > 0
    ? Object.fromEntries(Object.entries(k.parametros).sort(([a], [b]) => a.localeCompare(b))) : null,
]);

/** «manual», «sugestão do T-ratio (janela 0,50 m, limite 10)», «sugestão do agrupamento (2 grupos)». */
export function textoDaOrigem(origem: OrigemDaCamada, p: Record<string, number> | null): string {
  if (origem === 'sugestao_tratio') {
    return p && Number.isFinite(p.janela) && Number.isFinite(p.limite)
      ? `sugestão do T-ratio (janela ${numeroBr(p.janela, 2)} m, limite ${numero(p.limite, 2, true)})` : 'sugestão do T-ratio';
  }
  if (origem === 'sugestao_agrupamento') {
    return p && Number.isFinite(p.grupos) ? `sugestão do agrupamento (${plural(p.grupos, 'grupo', 'grupos')})` : 'sugestão do agrupamento';
  }
  return 'manual';
}

/** A camada que contém z (topo < z < base), para «Dividir no cursor». */
function camadaNoCursor(lidas: readonly CamadaLida[], z: number | null): CamadaLida | null {
  if (z === null || !Number.isFinite(z)) return null;
  return lidas.find((k) => z > k.topo + 0.005 && z < k.base - 0.005) ?? null;
}

/**
 * O bloqueio das ações (P-UI-24): as três à vista, bloqueadas, com o motivo — a permissão que falta ou a campanha
 * parada — escrito uma vez só para o grupo (rodada do agent-browser da 313).
 */
function useAcoesBloqueadas(c: CampanhaCompleta, bloqueio: 'permissao' | 'parada' | null): (textos: string[]) => ReactNode {
  const { eu } = useSessao();
  return (textos: string[]) => (bloqueio ? (
    <AcoesBloqueadas textos={textos} motivo={motivoDoBloqueio(bloqueio, 'não sugere nem grava as camadas da vertical CPTu',
      c.campanha.situacao, eu?.atuandoPor ? eu.nome : null)} />
  ) : null);
}

/** Onde o foco vai quando a ação apertada some (a sugestão, o rascunho): as ações do fim do painel (I2). */
const ACOES_DAS_CAMADAS = 'section.cptu-camadas > .cptu-edicao-acoes > button';

/**
 * O lado da vista Camadas: as vigentes e o rascunho, «Dividir no cursor», as duas sugestões (com os parâmetros à
 * vista), a sugestão desenhada e «Aceitar a sugestão», e «Gravar as camadas».
 */
export function CamadasDoLado({
  c, v, r, cursorZ, linhas, atuais, conferencia, sugestao, sugestaoSobreRascunho, estreito, bloqueio, aoMudar, aoSugerir,
  aoGravado, escalas, aoMudarEscala, sobreRascunho = false,
}: {
  c: CampanhaCompleta;
  v: VerticalCompleta;
  /** O resultado que a tela mostra (o do rascunho da interpretação, ou o gravado). */
  r: Resultado;
  cursorZ: number | null;
  /** O rascunho das camadas; null = sem rascunho (valem as vigentes). */
  linhas: LinhaDaCamada[] | null;
  /** As linhas da tela — o rascunho, ou as vigentes — com as mesmas chaves da conferência. */
  atuais: readonly LinhaDaCamada[];
  conferencia: ConferenciaDasCamadas;
  sugestao: SugestaoDeCamadas | null;
  /** A sugestão foi calculada sobre o rascunho da interpretação (não gravado). */
  sugestaoSobreRascunho: boolean;
  estreito: boolean;
  bloqueio: 'permissao' | 'parada' | null;
  aoMudar(l: LinhaDaCamada[] | null): void;
  aoSugerir(s: SugestaoDeCamadas | null): void;
  aoGravado(): Promise<void>;
  /** A escala horizontal dos gráficos (onda 347, R12), para as trilhas da janela «Camadas automáticas». */
  escalas?: Readonly<Record<string, EscalaDoGrafico>>;
  aoMudarEscala?(grandeza: string, e: EscalaDoGrafico | null): void;
  /** O `r` é o do rascunho da interpretação (não gravado): a janela «Camadas automáticas» diz, e o lote usa o gravado. */
  sobreRascunho?: boolean;
}) {
  const acoesBloqueadas = useAcoesBloqueadas(c, bloqueio);
  // A janela «Camadas automáticas» (onda 347, Tarefa 10): também para ler, no celular e com a campanha parada.
  const [automaticas, setAutomaticas] = useState(false);
  useFocoDeVolta(automaticas, '.cptu-botao-automaticas');
  // Os valores com que a tela começa, escritos nos campos (R13: escolhas do motor, sem fonte — a assinar pelo RT).
  const [janelaTxt, setJanelaTxt] = useState(noCampo(TRATIO_INICIAL.janela));
  const [limiteTxt, setLimiteTxt] = useState(paraCampo(TRATIO_INICIAL.limite, 2));
  const [porNumero, setPorNumero] = useState(false);
  const [gruposTxt, setGruposTxt] = useState('');
  const [gravando, setGravando] = useState(false);
  const idDoDividir = useId();
  const idDoGravar = useId();
  const idDoTRatio = useId();
  const idDoAgrupamento = useId();
  const soLer = estreito || !!bloqueio;
  // A janela devolve o foco a quem a abriu (rodada do agent-browser da 313).
  useFocoDeVolta(gravando, ACOES_DAS_CAMADAS);
  const vigentes = v.camadas ?? [];
  const retiradas = v.camadas_retiradas ?? 0;

  const resumo = (
    <p className="cptu-nota">
      {vigentes.length === 0 ? 'Nenhuma camada gravada.' : `${plural(vigentes.length, 'camada vigente', 'camadas vigentes')}.`}
      {retiradas > 0 ? ` ${plural(retiradas, 'camada retirada', 'camadas retiradas')} — ficam no histórico.` : ''}
      {linhas ? ' O rascunho das camadas ainda não foi gravado.' : ''}
    </p>
  );
  const cabeca = (
    <header className="cptu-painel-cabeca">
      <h3>Camadas</h3>
      <p className="cptu-nota">
        As camadas que valem no relatório são as gravadas pelo engenheiro; as sugestões do motor são só sugestões, pedidas
        por botão e nunca sozinhas.
      </p>
      {resumo}
    </header>
  );
  const janelaAutomatica = automaticas ? (
    <JanelaDasCamadasAutomaticas c={c} v={v} r={r} estreito={estreito} bloqueio={bloqueio} sobreRascunho={sobreRascunho}
      escalas={escalas} aoMudarEscala={aoMudarEscala} aoFechar={() => setAutomaticas(false)}
      aoLevar={(l) => { aoMudar(l); aoSugerir(null); setAutomaticas(false); }}
      aoGravado={async () => { setAutomaticas(false); await aoGravado(); }} />
  ) : null;
  const botaoAutomaticas = (
    <button type="button" className="botao secundario cptu-botao-automaticas" aria-haspopup="dialog"
      onClick={() => setAutomaticas(true)}>
      Camadas automáticas
    </button>
  );
  if (soLer) {
    return (
      <section className="cptu-camadas" aria-label="Camadas da vertical">
        {cabeca}
        <div className="cptu-camadas-acoes">{botaoAutomaticas}</div>
        {janelaAutomatica}
        {bloqueio && !estreito && (
          <div className="cptu-edicao-acoes">
            {acoesBloqueadas(['Sugerir por T-ratio', 'Sugerir por agrupamento', 'Gravar as camadas'])}
          </div>
        )}
      </section>
    );
  }

  const noCursor = camadaNoCursor(conferencia.lidas, cursorZ);
  const motivoDoDividir = cursorZ === null ? 'Ponha o cursor nas trilhas, dentro de uma camada, para dividi-la ali.'
    : noCursor ? null : `O cursor (${numeroBr(cursorZ, 2)} m) não está dentro de uma camada: ponha-o entre o topo e a base de uma.`;
  const dividir = () => {
    if (!noCursor || cursorZ === null) return;
    const z = noCampo(cursorZ);
    const novas: LinhaDaCamada[] = [];
    for (const l of atuais) {
      if (l.chave !== noCursor.chave) { novas.push(l); continue; }
      novas.push({ ...l, base: z, origem: 'manual', parametros: null });
      novas.push({ ...l, chave: novaChave(), topo: z, origem: 'manual', parametros: null });
    }
    aoMudar(novas);
  };
  const acrescentar = () => {
    const ultima = conferencia.lidas[conferencia.lidas.length - 1];
    const z0 = r.n > 0 ? r.col.z[0] : 0;
    const topo = ultima ? ultima.base : duasCasas(z0);
    const fim = v.profundidade_final ?? (r.n > 0 ? r.col.z[r.n - 1] : topo + 1);
    const base = fim > topo ? duasCasas(fim) : duasCasas(topo + 1);
    aoMudar([...atuais, {
      chave: novaChave(), topo: noCampo(topo), base: noCampo(base), unidade: `U${atuais.length + 1}`, descricao: '',
      origem: 'manual', parametros: null,
    }]);
  };

  const janela = lerProfundidade(janelaTxt);
  const limite = lerProfundidade(limiteTxt);
  const problemaDoTRatio = janela === undefined || !(janela > 0) ? 'a janela é um número de metros maior que zero'
    : limite === undefined ? 'diga o limite (não há limite padrão)' : null;
  const grupos = lerProfundidade(gruposTxt);
  const problemaDoAgrupamento = porNumero && !gruposServem(grupos) ? PROBLEMA_DOS_GRUPOS : null;

  const mudou = linhas !== null;
  const motivoDoGravar = !mudou ? 'Nada mudou nas camadas ainda.'
    : conferencia.quantosProblemas > 0
      ? `Há ${plural(conferencia.quantosProblemas, 'problema', 'problemas')} nas camadas: confira as linhas marcadas.`
      : null;

  return (
    <section className="cptu-camadas" aria-label="Camadas da vertical">
      {cabeca}
      <div className="cptu-camadas-acoes">
        <button type="button" className="botao secundario" aria-disabled={motivoDoDividir ? true : undefined}
          aria-describedby={motivoDoDividir ? idDoDividir : undefined} onClick={dividir}>
          Dividir no cursor{cursorZ !== null ? ` (${numeroBr(cursorZ, 2)} m)` : ''}
        </button>
        <button type="button" className="botao secundario" onClick={acrescentar}>＋ Camada</button>
        {botaoAutomaticas}
        {motivoDoDividir && <small id={idDoDividir} className="cptu-motivo-bloqueio">{motivoDoDividir}</small>}
      </div>
      {janelaAutomatica}

      <fieldset className="cptu-edicao-grupo" data-sugestao="tratio">
        <legend>Sugerir por T-ratio (sobre o Ic, sem espessura mínima)</legend>
        <div className="cptu-edicao-campos">
          <CampoNumerico rotulo="Janela (m)" value={janelaTxt} onChange={(e) => setJanelaTxt(e.target.value)} />
          <CampoNumerico rotulo="Limite" value={limiteTxt} onChange={(e) => setLimiteTxt(e.target.value)} />
        </div>
        <p className="cptu-nota">valores iniciais escolhidos pelo motor, sem fonte — a assinar pelo RT</p>
        {/* Os dois caminhos dão resultados diferentes de propósito (revisão final da 347): o cálculo daqui não muda. */}
        <p className="cptu-nota" data-nota="tratio-ic">
          Esta caixa usa o Ic e não aplica espessura mínima. A janela «Camadas automáticas» usa os parâmetros guardados (de
          partida, log qt e espessura mínima de 0,50 m): por isso as duas podem sugerir camadas diferentes.
        </p>
        {problemaDoTRatio && <Aviso tipo="atencao"><span id={idDoTRatio}>{problemaDoTRatio}.</span></Aviso>}
        <button type="button" className="botao secundario" aria-disabled={problemaDoTRatio ? true : undefined}
          aria-describedby={problemaDoTRatio ? idDoTRatio : undefined}
          onClick={() => {
            if (problemaDoTRatio || janela === undefined || limite === undefined) return;
            aoSugerir(sugerirPorTRatio(r, { janela, limite }));
          }}>
          Sugerir por T-ratio
        </button>
      </fieldset>

      <fieldset className="cptu-edicao-grupo" data-sugestao="agrupamento">
        <legend>Sugerir por agrupamento (log qt e log Fr)</legend>
        <div className="cptu-escolha-grupos" role="radiogroup" aria-label="Quantos grupos">
          <label className="cptu-radio">
            <input type="radio" name="grupos-do-agrupamento" checked={!porNumero} onChange={() => setPorNumero(false)} />
            <span>pelo maior ganho</span>
          </label>
          <label className="cptu-radio">
            <input type="radio" name="grupos-do-agrupamento" checked={porNumero} onChange={() => setPorNumero(true)} />
            <span>{ESCOLHA_DOS_GRUPOS}</span>
          </label>
        </div>
        {porNumero && (
          <CampoNumerico rotulo="Grupos" value={gruposTxt} inteiro onChange={(e) => setGruposTxt(e.target.value)} />
        )}
        {problemaDoAgrupamento && <Aviso tipo="atencao"><span id={idDoAgrupamento}>{problemaDoAgrupamento}.</span></Aviso>}
        <button type="button" className="botao secundario" aria-disabled={problemaDoAgrupamento ? true : undefined}
          aria-describedby={problemaDoAgrupamento ? idDoAgrupamento : undefined}
          onClick={() => {
            if (problemaDoAgrupamento) return;
            aoSugerir(sugerirPorAgrupamento(r, porNumero && grupos !== undefined ? { grupos } : undefined));
          }}>
          Sugerir por agrupamento
        </button>
      </fieldset>

      {/* A caixa some ao aceitar ou limpar: o foco vai às ações do fim do painel, nunca ao <body> (I2). */}
      {sugestao && <CaixaDaSugestao sugestao={sugestao} sobreRascunho={sugestaoSobreRascunho}
        aoAceitar={() => { aoMudar(linhasDaSugestao(sugestao)); aoSugerir(null); focarOPrimeiro(ACOES_DAS_CAMADAS); }}
        aoLimpar={() => { aoSugerir(null); focarOPrimeiro(ACOES_DAS_CAMADAS); }} />}

      <div className="cptu-edicao-acoes">
        {mudou && (
          <button type="button" className="botao secundario" onClick={() => { aoMudar(null); focarOPrimeiro(ACOES_DAS_CAMADAS); }}>
            Sair sem gravar as camadas
          </button>
        )}
        <button type="button" className="botao" aria-disabled={motivoDoGravar ? true : undefined}
          aria-describedby={motivoDoGravar ? idDoGravar : undefined}
          onClick={() => { if (!motivoDoGravar) setGravando(true); }}>
          Gravar as camadas
        </button>
        {motivoDoGravar && <small id={idDoGravar} className="cptu-motivo-bloqueio">{motivoDoGravar}</small>}
      </div>
      {gravando && (
        <JanelaDasCamadas v={v} lidas={conferencia.lidas} aoFechar={() => setGravando(false)}
          aoPronto={async () => { setGravando(false); await aoGravado(); }} />
      )}
    </section>
  );
}

/** A sugestão desenhada: o método e os parâmetros, as fronteiras, os avisos do motor, «Aceitar» e «Limpar». */
function CaixaDaSugestao({ sugestao, sobreRascunho, aoAceitar, aoLimpar }: {
  sugestao: SugestaoDeCamadas; sobreRascunho: boolean; aoAceitar(): void; aoLimpar(): void;
}) {
  const p = sugestao.parametros;
  const titulo = sugestao.metodo === 'tratio'
    ? `Sugestão do T-ratio (janela ${numeroBr(p.janela, 2)} m, limite ${numero(p.limite, 2, true)})`
    : `Sugestão do agrupamento (${Number.isFinite(p.grupos) ? plural(p.grupos, 'grupo', 'grupos') : 'sem grupos'})`;
  return (
    <div className="cptu-caixa-sugestao" role="status" data-metodo={sugestao.metodo}>
      <p className="cptu-caixa-sugestao-titulo"><b>{titulo}</b></p>
      {sobreRascunho && (
        <Aviso tipo="atencao" titulo="Calculada sobre o rascunho (não gravado)">
          As premissas ou a edição que a tela mostra ainda não foram gravadas.
        </Aviso>
      )}
      <p>
        {plural(sugestao.camadas.length, 'camada', 'camadas')} · {plural(sugestao.fronteiras.length, 'fronteira', 'fronteiras')}
        {sugestao.fronteiras.length > 0 ? ' (tracejadas nas trilhas):' : ''}
      </p>
      {sugestao.fronteiras.length > 0 && (
        <p className="cptu-fronteiras-sugeridas" data-fronteiras={sugestao.fronteiras.map((z) => z.toFixed(2)).join(' ')}>
          {/* O número e a unidade não se separam na quebra de linha (o espaço inseparável). */}
          {sugestao.fronteiras.map((z) => `${numeroBr(z, 2)}\u00a0m`).join(' · ')}
        </p>
      )}
      {sugestao.avisos.length > 0 && (
        <ul className="cptu-avisos-da-sugestao">{sugestao.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
      )}
      <p className="cptu-nota">Sugestão do motor, fora do RN-252: as escolhas dele vão ao RT como regras a assinar.</p>
      <div className="cptu-edicao-acoes">
        {/* Sem camadas (um parâmetro que não serve), não há o que aceitar: os avisos acima dizem por quê. */}
        {sugestao.camadas.length > 0 && (
          <button type="button" className="botao secundario" onClick={aoAceitar}>Aceitar a sugestão</button>
        )}
        <button type="button" className="ligacao" onClick={aoLimpar}>Limpar a sugestão</button>
      </div>
    </div>
  );
}

/** O motivo e o que muda: as que ficam, as que entram e as que saem (retiradas — ficam no histórico). */
function JanelaDasCamadas({ v, lidas, aoFechar, aoPronto }: {
  v: VerticalCompleta; lidas: readonly CamadaLida[]; aoFechar(): void; aoPronto(): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const vigentes = (v.camadas ?? []).map((k) => ({
    topo: Number(k.topo_m), base: Number(k.base_m), unidade: k.unidade, descricao: k.descricao, origem: k.origem,
    parametros: k.parametros,
  }));
  const daVigente = new Set(vigentes.map(identidade));
  const novas = new Set(lidas.map(identidade));
  const ficam = lidas.filter((k) => daVigente.has(identidade(k))).length;
  const entram = lidas.length - ficam;
  const saem = vigentes.filter((k) => !novas.has(identidade(k)));
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha.' : null;
  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const corpo: GravacaoDasCamadas = {
        camadas: lidas.map((k) => ({
          topo: k.topo, base: k.base, unidade: k.unidade, descricao: k.descricao, origem: k.origem,
          parametros: k.parametros && Object.keys(k.parametros).length > 0 ? k.parametros : null,
        })),
        motivo: motivo.trim(),
      };
      await gravarCamadas(v.id, corpo);
      await aoPronto();
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };
  return (
    <Dialogo aberto titulo="Gravar as camadas" rotulo={`Vertical ${v.codigo}`} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          Vai o conjunto inteiro da vertical {v.codigo}: {plural(lidas.length, 'camada', 'camadas')}. Nada se apaga — a
          camada que sai é retirada e fica no histórico, com quem a tirou e quando.
        </p>
        <ul className="cptu-lista-da-janela">
          <li>{plural(ficam, 'fica como está', 'ficam como estão')}</li>
          <li>{plural(entram, 'entra', 'entram')}</li>
          <li>
            {plural(saem.length, 'sai (retirada)', 'saem (retiradas)')}
            {saem.length > 0 ? `: ${saem.map((k) => `${k.unidade} (${faixa(k.topo, k.base)})`).join('; ')}` : ''}
          </li>
        </ul>
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/* ── A janela «Camadas automáticas» (onda 347, Tarefa 10; spec 2026-10-22 §6, R11) ─────────────────────────────── */

/** O método da janela: o T-ratio (sobre o Ic ou log qt) e o agrupamento contíguo de hoje (o NGL entra na onda própria). */
type MetodoDaJanela = 'tratio' | 'agrupamento';

const ROTULO_DO_METODO: Readonly<Record<MetodoDaJanela, string>> = Object.freeze({
  tratio: 'T-ratio · Wickremesinghe & Campanella (1991)',
  agrupamento: 'Agrupamento contíguo · Ward/CONISS',
});

/** A chave de uma camada sugerida (topo e base ao centímetro): a unidade trocada pelo engenheiro fica com ela. */
const chaveDaCamada = (k: { topo: number; base: number }): string => `${duasCasas(k.topo)}|${duasCasas(k.base)}`;

/** «de 0,05 a 20 m» — a faixa aceita de um parâmetro, como a tela escreve. */
const faixaDoParametro = (f: { min: number; max: number }, unidade: string): string =>
  `de ${numero(f.min, 2, true)} a ${numero(f.max, 2, true)}${unidade}`;

/** O número do campo dentro da faixa, ou o problema. */
function lerParametro(texto: string, f: { min: number; max: number }, nome: string, unidade: string): { v?: number; problema?: string } {
  const v = lerProfundidade(texto);
  if (v === undefined || v < f.min || v > f.max) return { problema: `${nome} vai ${faixaDoParametro(f, unidade)}` };
  return { v };
}

/** A sugestão de uma vertical pelo método e os parâmetros da janela; null quando um parâmetro não serve. */
type Sugerir = (r: Resultado) => SugestaoDeCamadas & { custos?: { grupos: number; custo: number }[] };

/**
 * A janela «Camadas automáticas»: o método e os parâmetros (os do T-ratio começam nos guardados no aparelho, R5, e voltam a
 * ele a cada mudança que vale — a sugestão do 3D os lê), as trilhas da sugestão (com o zoom e a escala), a tabela com a
 * unidade de cada camada (a zona SBTn predominante, que o engenheiro troca), «Levar ao rascunho das camadas» e, com
 * «Aplicar a todas as verticais da campanha», a lista vertical a vertical e «Gravar as camadas de todas as verticais»
 * (motivo; `PUT /cptu/campanhas/:id/camadas`, tudo ou nada). No celular, só para ler (sem «Levar» nem «Gravar»); com a
 * campanha parada ou sem a permissão, as duas ações à vista, bloqueadas, com o motivo (P-UI-24).
 */
function JanelaDasCamadasAutomaticas({
  c, v, r, estreito, bloqueio, sobreRascunho, escalas, aoMudarEscala, aoFechar, aoLevar, aoGravado,
}: {
  c: CampanhaCompleta;
  v: VerticalCompleta;
  r: Resultado;
  estreito: boolean;
  sobreRascunho: boolean;
  bloqueio: 'permissao' | 'parada' | null;
  escalas?: Readonly<Record<string, EscalaDoGrafico>>;
  aoMudarEscala?(grandeza: string, e: EscalaDoGrafico | null): void;
  aoFechar(): void;
  aoLevar(l: LinhaDaCamada[]): void;
  aoGravado(): Promise<void>;
}) {
  const acoesBloqueadas = useAcoesBloqueadas(c, bloqueio);
  const quem = useSessao().eu?.usuarioId ?? null;
  const guardados = useMemo(() => parametrosDoTRatioGuardados(quem), [quem]);
  const [metodo, setMetodo] = useState<MetodoDaJanela>('tratio');
  const [grandeza, setGrandeza] = useState<'ic' | 'log_qt'>(guardados.grandeza ?? 'log_qt');
  const [janelaTxt, setJanelaTxt] = useState(noCampo(guardados.janela));
  const [limiteTxt, setLimiteTxt] = useState(paraCampo(guardados.limite, 2));
  const [espessuraTxt, setEspessuraTxt] = useState(noCampo(guardados.espessuraMinima ?? 0));
  const [juntar, setJuntar] = useState(guardados.juntarPorZona === true);
  const [porNumero, setPorNumero] = useState(false);
  const [gruposTxt, setGruposTxt] = useState('');
  const [trocadas, setTrocadas] = useState<ReadonlyMap<string, string>>(new Map());
  const [aTodas, setATodas] = useState(false);
  const [zoom, setZoom] = useState<TrechoDoZoom | null>(null);
  const idDoProblema = useId();
  const idDoLevar = useId();
  const soLer = estreito || !!bloqueio;
  // Os parâmetros só vão ao aparelho quando alguém os muda aqui (abrir a janela não grava nada; no celular e com a
  // campanha parada a janela é de ler, e também não grava).
  const [mexeu, setMexeu] = useState(false);
  const mexer = <T,>(f: (x: T) => void) => (x: T) => { setMexeu(true); f(x); };

  // Trocar a grandeza com os valores de partida da outra nos campos troca também a janela e o limite de partida.
  const trocarGrandeza = (g: 'ic' | 'log_qt') => {
    const de = g === 'ic' ? TRATIO_INICIAL_QT : TRATIO_INICIAL;
    const para = g === 'ic' ? TRATIO_INICIAL : TRATIO_INICIAL_QT;
    if (lerProfundidade(janelaTxt) === de.janela && lerProfundidade(limiteTxt) === de.limite) {
      setJanelaTxt(noCampo(para.janela));
      setLimiteTxt(paraCampo(para.limite, 2));
    }
    setGrandeza(g);
  };

  const f = FAIXAS_DOS_PARAMETROS_DO_TRATIO;
  const janela = lerParametro(janelaTxt, f.janela, 'a janela', ' m');
  const limite = lerParametro(limiteTxt, f.limite, 'o limite de T', '');
  const espessura = lerParametro(espessuraTxt, f.espessuraMinima, 'a espessura mínima', ' m');
  const grupos = lerProfundidade(gruposTxt);
  const problemas = metodo === 'tratio'
    ? [janela.problema, limite.problema, espessura.problema].filter((x): x is string => !!x)
    : porNumero && !gruposServem(grupos) ? [PROBLEMA_DOS_GRUPOS] : [];
  const parametros: ParametrosDoTRatio | null = metodo === 'tratio' && problemas.length === 0
    && janela.v !== undefined && limite.v !== undefined && espessura.v !== undefined
    ? {
      janela: janela.v, limite: limite.v, grandeza, espessuraMinima: espessura.v, juntarPorZona: juntar,
      // O espaçamento mínimo entre picos não tem campo aqui: vale o guardado (o mesmo que a sugestão do 3D usa).
      ...(guardados.espacamentoMinimo !== undefined ? { espacamentoMinimo: guardados.espacamentoMinimo } : {}),
    } : null;
  const chaveDosParametros = parametros ? JSON.stringify(parametros) : null;

  // Os parâmetros que valem, mudados aqui, vão ao aparelho (R5) e avisam a sugestão do 3D (R33).
  useEffect(() => {
    if (mexeu && !soLer && chaveDosParametros) gravarParametrosDoTRatio(quem, JSON.parse(chaveDosParametros) as ParametrosDoTRatio);
  }, [mexeu, soLer, chaveDosParametros, quem]);

  const sugerir: Sugerir | null = useMemo(() => {
    if (metodo === 'tratio') {
      if (!chaveDosParametros) return null;
      const x = JSON.parse(chaveDosParametros) as ParametrosDoTRatio;
      return (rr: Resultado) => sugerirPorTRatio(rr, x);
    }
    if (problemas.length > 0) return null;
    const pedido = porNumero && grupos !== undefined ? { grupos } : undefined;
    return (rr: Resultado) => sugerirPorAgrupamento(rr, pedido);
    // `problemas` sai dos textos de que as dependências já dependem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metodo, chaveDosParametros, porNumero, grupos]);
  const sugestao = useMemo(() => (sugerir ? sugerir(r) : null), [sugerir, r]);
  const daZona = useMemo(() => (sugestao ? comUnidadePelaZona(sugestao.camadas, r) : []), [sugestao, r]);
  const unidades = daZona.map((k) => trocadas.get(chaveDaCamada(k)) ?? k.unidade);
  const medias = useMemo(() => (sugestao ? mediasDasCamadas(r, sugestao) : []), [sugestao, r]);
  const g = sugestao ? grandezaDaTrilha(sugestao) : null;

  // As unidades trocadas na tabela que valem agora (camada da sugestão de hoje, nome diferente do da zona).
  // Memorizada: ela entra nas dependências da lista do lote, que calcula a sugestão de todas as verticais.
  const trocadasNaTabela = useMemo(() => daZona.flatMap((k) => {
    const u = (trocadas.get(chaveDaCamada(k)) ?? k.unidade).trim();
    return u && u !== k.unidade ? [{ chave: chaveDaCamada(k), unidade: u }] : [];
  }), [daZona, trocadas]);
  const trocar = (k: { topo: number; base: number }, unidade: string) => {
    setTrocadas((m) => new Map(m).set(chaveDaCamada(k), unidade));
  };
  const semUnidade = unidades.some((u) => !u.trim());
  const motivoDoLevar = !sugestao || sugestao.camadas.length === 0
    ? 'A sugestão não tem camadas: os avisos dizem por quê.'
    : semUnidade ? 'Dê o nome da unidade de todas as camadas.' : null;
  const levar = () => {
    if (motivoDoLevar || !sugestao) return;
    aoLevar(daZona.map((k, i) => ({
      chave: novaChave(), topo: noCampo(k.topo), base: noCampo(k.base), unidade: unidades[i].trim(), descricao: '',
      origem: k.origem, parametros: { ...sugestao.parametros },
    })));
  };

  return (
    <Dialogo aberto larga titulo="Camadas automáticas" rotulo={`Vertical ${v.codigo}`} aoFechar={aoFechar}>
      <div className="cptu-camadas-automaticas" data-metodo={metodo}>
        <p className="sub">
          Sugestões do motor, fora do RN-252: viram camadas só depois de gravadas pelo engenheiro, com motivo.
          {estreito ? ' No celular, só para ler: as camadas se gravam no computador.' : ''}
        </p>
        <div className="cptu-camadas-automaticas-parametros">
          <label className="campo">
            <span className="rotulo">Método</span>
            <select value={metodo} onChange={(e) => { setMetodo(e.target.value as MetodoDaJanela); setZoom(null); }}>
              {(Object.keys(ROTULO_DO_METODO) as MetodoDaJanela[]).map((m) => (
                <option key={m} value={m}>{ROTULO_DO_METODO[m]}</option>
              ))}
            </select>
          </label>
          {metodo === 'tratio' ? (
            <>
              <label className="campo">
                <span className="rotulo">Sobre</span>
                <select value={grandeza} onChange={(e) => mexer(trocarGrandeza)(e.target.value as 'ic' | 'log_qt')}>
                  <option value="ic">Ic</option>
                  <option value="log_qt">log qt</option>
                </select>
              </label>
              <CampoNumerico rotulo="Janela (m)" value={janelaTxt} onChange={(e) => mexer(setJanelaTxt)(e.target.value)} />
              <CampoNumerico rotulo="Limite de T" value={limiteTxt} onChange={(e) => mexer(setLimiteTxt)(e.target.value)} />
              <CampoNumerico rotulo="Espessura mínima (m)" value={espessuraTxt}
                onChange={(e) => mexer(setEspessuraTxt)(e.target.value)} />
              <label className="cptu-radio cptu-camadas-automaticas-juntar">
                <input type="checkbox" checked={juntar} onChange={(e) => mexer(setJuntar)(e.target.checked)} />
                <span>Juntar vizinhas da mesma zona</span>
              </label>
            </>
          ) : (
            <>
              <div className="cptu-escolha-grupos" role="radiogroup" aria-label="Quantos grupos">
                <label className="cptu-radio">
                  <input type="radio" name="grupos-da-janela" checked={!porNumero} onChange={() => setPorNumero(false)} />
                  <span>pelo maior ganho</span>
                </label>
                <label className="cptu-radio">
                  <input type="radio" name="grupos-da-janela" checked={porNumero} onChange={() => setPorNumero(true)} />
                  <span>{ESCOLHA_DOS_GRUPOS}</span>
                </label>
              </div>
              {porNumero && (
                <CampoNumerico rotulo="Grupos" value={gruposTxt} inteiro onChange={(e) => setGruposTxt(e.target.value)} />
              )}
            </>
          )}
        </div>
        <p className="cptu-nota">
          {metodo === 'tratio'
            ? 'valores de partida sem fonte — a assinar pelo RT (guardados neste aparelho; a sugestão do 3D usa os mesmos)'
            : 'sobre log qt e log Fr, em blocos de 5 cm — escolhas do motor, sem fonte, a assinar pelo RT'}
        </p>
        {problemas.length > 0 && (
          <Aviso tipo="atencao"><span id={idDoProblema}>{problemas.join('; ')}.</span></Aviso>
        )}

        {sugestao && (
          <>
            {sobreRascunho && (
              <Aviso tipo="atencao" titulo="Calculada sobre o rascunho (não gravado)">
                As premissas ou a edição que a tela mostra ainda não foram gravadas: as trilhas e a tabela são do rascunho.
              </Aviso>
            )}
            {sugestao.avisos.length > 0 && (
              <ul className="cptu-avisos-da-sugestao">{sugestao.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
            )}
            <TrilhasDaSugestao r={r} sugestao={sugestao} unidades={unidades} zoom={zoom} aoZoom={setZoom}
              escalas={escalas} aoMudarEscala={aoMudarEscala} />
            <div className="rola">
              <table className="cptu-tabela-da-sugestao" aria-label="As camadas sugeridas">
                <thead>
                  <tr>
                    <th scope="col">Camada</th>
                    <th scope="col" className="n">Topo (m)</th>
                    <th scope="col" className="n">Base (m)</th>
                    <th scope="col" className="n">Espessura (m)</th>
                    <th scope="col" className="n">{g?.chave === 'qt' ? 'qt médio geom. (MPa)' : 'Ic médio'}</th>
                    <th scope="col">Zona SBTn</th>
                    <th scope="col">Unidade</th>
                  </tr>
                </thead>
                <tbody>
                  {daZona.map((k, i) => (
                    <tr key={chaveDaCamada(k)} data-camada={i + 1}>
                      <th scope="row">C{i + 1}</th>
                      <td className="n mono">{numeroBr(k.topo, 2)}</td>
                      <td className="n mono">{numeroBr(k.base, 2)}</td>
                      <td className="n mono">{numeroBr(k.base - k.topo, 2)}</td>
                      <td className="n mono" data-media={medias[i]}>
                        {Number.isFinite(medias[i]) ? textoDoValor(g?.chave === 'qt' ? 'qt' : 'ic', medias[i]) : '—'}
                      </td>
                      <td>{k.unidade}</td>
                      <td>
                        {soLer ? unidades[i] : (
                          <label className="campo cptu-campo-da-tabela">
                            <span className="so-leitor">C{i + 1}: unidade</span>
                            <input type="text" value={unidades[i]} maxLength={80}
                              onChange={(e) => trocar(k, e.target.value)} />
                          </label>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <label className="cptu-radio cptu-camadas-automaticas-todas">
          <input type="checkbox" checked={aTodas} onChange={(e) => setATodas(e.target.checked)} />
          <span>Aplicar a todas as verticais da campanha</span>
        </label>
        {aTodas && (
          <ListaDaCampanha c={c} atual={v} sugerir={sugerir} trocadas={trocadasNaTabela} sobreRascunho={sobreRascunho}
            soLer={soLer} aoGravado={aoGravado} />
        )}

        {bloqueio && !estreito && (
          <div className="cptu-edicao-acoes">
            {acoesBloqueadas(['Levar ao rascunho das camadas', 'Gravar as camadas de todas as verticais'])}
          </div>
        )}
        {!soLer && motivoDoLevar && <small id={idDoLevar} className="cptu-motivo-bloqueio">{motivoDoLevar}</small>}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>Fechar</button>
          {!soLer && (
            // Um primário só (P-UI-10): com «Aplicar a todas», o primário é o «Gravar» do lote.
            <button type="button" className={aTodas ? 'botao secundario' : 'botao'} aria-disabled={motivoDoLevar ? true : undefined}
              aria-describedby={motivoDoLevar ? idDoLevar : undefined} onClick={levar}>
              Levar ao rascunho das camadas
            </button>
          )}
        </div>
      </div>
    </Dialogo>
  );
}

/**
 * «Aplicar a todas as verticais da campanha»: o mesmo método e os mesmos parâmetros em cada vertical ativa (o resultado
 * gravado de cada uma, `useAnaliseDaCampanha`), a lista vertical a vertical com as camadas, e «Gravar as camadas de todas as
 * verticais», que pede o motivo e manda o lote. A vertical aberta leva as unidades trocadas na tabela.
 */
function ListaDaCampanha({ c, atual, sugerir, trocadas, sobreRascunho, soLer, aoGravado }: {
  c: CampanhaCompleta;
  atual: VerticalCompleta;
  sugerir: Sugerir | null;
  /** As unidades trocadas na tabela da vertical aberta, pela chave da camada (topo|base). */
  trocadas: readonly { chave: string; unidade: string }[];
  /** A tabela da janela é do rascunho da interpretação: o lote, do gravado — a lista diz. */
  sobreRascunho: boolean;
  /** No celular, ou bloqueada (o grupo bloqueado da janela já mostra o «Gravar» com o motivo): sem o botão. */
  soLer: boolean;
  aoGravado(): Promise<void>;
}) {
  const { verticais, erro } = useAnaliseDaCampanha(c);
  const [motivoAberto, setMotivoAberto] = useState(false);
  const idDoGravar = useId();
  useFocoDeVolta(motivoAberto, '.cptu-camadas-automaticas-todas input');
  const ids = useMemo(() => new Map(c.verticais.filter((x) => x.situacao === 'ativa').map((x) => [x.codigo, x.id])), [c.verticais]);
  const lista = useMemo(() => {
    if (!verticais || !sugerir) return null;
    const daTabela = new Map(trocadas.map((t) => [t.chave, t.unidade]));
    return verticais.map((x) => {
      const s = sugerir(x.r);
      const aberta = x.codigo === atual.codigo;
      const camadas = comUnidadePelaZona(s.camadas, x.r).map((k) => ({
        ...k, unidade: (aberta ? daTabela.get(chaveDaCamada(k)) : undefined) || k.unidade,
      }));
      // A unidade trocada cuja camada não existe na sugestão do gravado (o rascunho mudou as fronteiras) não entra.
      const chaves = new Set(camadas.map(chaveDaCamada));
      const perdidas = aberta ? trocadas.filter((t) => !chaves.has(t.chave)).map((t) => t.unidade) : [];
      return {
        codigo: x.codigo, id: ids.get(x.codigo) ?? null, camadas, parametros: s.parametros, aviso: s.avisos[0] ?? null, perdidas,
        acima: camadas.length > LIMITE_DE_CAMADAS,
      };
    });
  }, [verticais, sugerir, trocadas, atual.codigo, ids]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!sugerir) return <p className="cptu-nota">Acerte os parâmetros para a sugestão de cada vertical.</p>;
  if (!lista) return <Carregando texto="Calculando as verticais da campanha" />;
  const comCamadas = lista.filter((x) => x.camadas.length > 0 && x.id);
  // Os tetos da API (o lote é tudo ou nada): a tela bloqueia antes de mandar, dizendo qual vertical passa.
  const acima = comCamadas.filter((x) => x.acima).map((x) => x.codigo);
  const motivoDoGravar = comCamadas.length === 0 ? 'Nenhuma vertical com camada sugerida.'
    : acima.length > 0
      ? `${acima.join(', ')} ${acima.length === 1 ? 'passa' : 'passam'} de ${numero(LIMITE_DE_CAMADAS, 0)} camadas por vertical (o teto da `
        + 'gravação): mude os parâmetros para camadas mais grossas.'
      : comCamadas.length > LIMITE_DE_VERTICAIS_NO_LOTE
        ? `São ${numero(comCamadas.length, 0)} verticais: o lote leva no máximo ${numero(LIMITE_DE_VERTICAIS_NO_LOTE, 0)} de uma vez.`
        : null;
  return (
    <section className="cptu-camadas-da-campanha" aria-label="As camadas de todas as verticais">
      <p className="cptu-nota">
        {plural(comCamadas.length, 'vertical com camadas sugeridas', 'verticais com camadas sugeridas')} de {lista.length}
        {' '}(o resultado gravado de cada uma). As vigentes de cada vertical serão trocadas pelas sugeridas.
      </p>
      {sobreRascunho && (
        <Aviso tipo="atencao" titulo="O lote usa o resultado gravado">
          A tabela acima é do rascunho da interpretação da {atual.codigo}, que não vai ao lote: cada vertical, inclusive a
          {' '}{atual.codigo}, entra com a sugestão sobre o resultado gravado dela.
        </Aviso>
      )}
      <ul className="cptu-lista-das-verticais">
        {lista.map((x) => (
          <li key={x.codigo} data-vertical={x.codigo} data-camadas={x.camadas.length} data-acima={x.acima ? 'sim' : undefined}
            data-perdidas={x.perdidas.length || undefined}>
            <b>{x.codigo}</b>
            {' — '}
            {x.camadas.length > 0 ? plural(x.camadas.length, 'camada', 'camadas') : `sem camada sugerida (fica de fora)${x.aviso ? `: ${x.aviso}` : ''}`}
            {x.acima && (
              <span className="cptu-marca-do-lote">
                {' '}<span className="cptu-marca-texto">▲</span> passa de {numero(LIMITE_DE_CAMADAS, 0)} camadas: o lote não grava assim
              </span>
            )}
            {x.perdidas.length > 0 && (
              <span className="cptu-marca-do-lote">
                <span className="cptu-marca-texto">▲</span>{' '}
                {plural(x.perdidas.length, 'unidade trocada na tabela não entra', 'unidades trocadas na tabela não entram')}
                {` (${x.perdidas.join('; ')}): a camada não existe na sugestão sobre o resultado gravado.`}
              </span>
            )}
            {x.camadas.length > 0 && (
              <span className="cptu-camadas-da-vertical">
                {x.camadas.map((k) => `${k.unidade} (${faixa(k.topo, k.base)})`).join('; ')}
              </span>
            )}
          </li>
        ))}
      </ul>
      {!soLer && (
        <div className="cptu-edicao-acoes">
          <button type="button" className="botao" aria-disabled={motivoDoGravar ? true : undefined}
            aria-describedby={motivoDoGravar ? idDoGravar : undefined}
            onClick={() => { if (!motivoDoGravar) setMotivoAberto(true); }}>
            Gravar as camadas de todas as verticais
          </button>
          {motivoDoGravar && <small id={idDoGravar} className="cptu-motivo-bloqueio">{motivoDoGravar}</small>}
        </div>
      )}
      {motivoAberto && (
        <JanelaDoLote c={c} verticais={comCamadas.map((x) => ({
          verticalId: x.id as string, codigo: x.codigo,
          camadas: x.camadas.map((k) => ({
            topo: duasCasas(k.topo), base: duasCasas(k.base), unidade: k.unidade, descricao: null, origem: k.origem,
            parametros: { ...x.parametros },
          })),
        }))} aoFechar={() => setMotivoAberto(false)}
          aoPronto={async () => { setMotivoAberto(false); await aoGravado(); }} />
      )}
    </section>
  );
}

/** O motivo do lote e o envio: tudo numa transação; a recusa (de uma vertical, ou da campanha) volta escrita. */
function JanelaDoLote({ c, verticais, aoFechar, aoPronto }: {
  c: CampanhaCompleta;
  verticais: { verticalId: string; codigo: string; camadas: GravacaoDasCamadas['camadas'] }[];
  aoFechar(): void;
  aoPronto(): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha de cada vertical.' : null;
  const total = verticais.reduce((n, x) => n + x.camadas.length, 0);
  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      await gravarCamadasDaCampanha(c.campanha.id, {
        motivo: motivo.trim(),
        verticais: verticais.map((x) => ({ verticalId: x.verticalId, camadas: x.camadas })),
      });
      await aoPronto();
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };
  return (
    // Gravando, nem «Cancelar» nem Esc: o lote já foi (I2 da 313; o padrão das outras janelas que gravam).
    <Dialogo aberto titulo="Gravar as camadas de todas as verticais" rotulo={`Campanha ${codigoDaCampanha(c.campanha.sequencia)}`}
      aoFechar={() => { if (!ocupado) aoFechar(); }}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          Vão {plural(verticais.length, 'vertical', 'verticais')}, {plural(total, 'camada', 'camadas')}, numa gravação só: todas
          ou nenhuma. Em cada vertical, a camada vigente que não vier é retirada e fica no histórico.
        </p>
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha de cada vertical, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar} disabled={ocupado}>Cancelar</button>
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}

/**
 * A tabela das camadas, embaixo das trilhas: as vigentes (ou o rascunho) com topo, base, unidade, descrição e origem;
 * para editar, os campos, «Juntar com a de baixo» e «Tirar», e o problema de cada linha logo embaixo dela.
 */
export function TabelaDasCamadas({ linhas, conferencia, soLer, aoMudar }: {
  linhas: readonly LinhaDaCamada[];
  conferencia: ConferenciaDasCamadas;
  soLer: boolean;
  aoMudar(l: LinhaDaCamada[]): void;
}) {
  if (linhas.length === 0) {
    return (
      <section className="cartao cptu-tabela-camadas" aria-label="As camadas da vertical">
        <h3 className="cptu-titulo-da-secao">As camadas</h3>
        <p className="cptu-nota">
          Nenhuma camada{soLer ? '.' : ': peça uma sugestão, ou «＋ Camada» para escrever a primeira.'}
        </p>
      </section>
    );
  }
  // «A de baixo» é a seguinte na ordem do topo.
  const ordem = [...linhas].map((l, i) => ({ l, i, topo: lerProfundidade(l.topo) ?? Number.POSITIVE_INFINITY }))
    .sort((a, b) => a.topo - b.topo || a.i - b.i);
  const trocar = (chave: number, mudanca: Partial<LinhaDaCamada>) => aoMudar(linhas.map((l) => {
    if (l.chave !== chave) return l;
    const nova = { ...l, ...mudanca };
    // Mudar a fronteira faz dela uma camada do engenheiro; o nome e a descrição não mudam a origem.
    if ((mudanca.topo !== undefined && mudanca.topo !== l.topo) || (mudanca.base !== undefined && mudanca.base !== l.base)) {
      nova.origem = 'manual';
      nova.parametros = null;
    }
    return nova;
  }));
  const juntar = (k: number) => {
    const cima = ordem[k].l;
    const baixo = ordem[k + 1]?.l;
    if (!baixo) return;
    const unida: LinhaDaCamada = { ...cima, base: baixo.base, origem: 'manual', parametros: null };
    aoMudar(ordem.filter((_, j) => j !== k + 1).map((x, j) => (j === k ? unida : x.l)));
  };
  const tirar = (chave: number) => aoMudar(ordem.map((x) => x.l).filter((l) => l.chave !== chave));
  return (
    <section className="cartao cptu-tabela-camadas" aria-label="As camadas da vertical">
      <h3 className="cptu-titulo-da-secao">
        As camadas{soLer ? '' : ' — o rascunho'}
      </h3>
      <div className="rola">
        <table aria-label="Camadas da vertical">
          <thead>
            <tr>
              <th scope="col">{soLer ? 'Unidade' : 'Unidade e origem'}</th>
              <th scope="col" className="n">Topo (m)</th>
              <th scope="col" className="n">Base (m)</th>
              <th scope="col">Descrição</th>
              <th scope="col">{soLer ? 'Origem' : <span className="so-leitor">Ações</span>}</th>
            </tr>
          </thead>
          {ordem.map(({ l }, k) => {
            const problemas = conferencia.problemas.get(l.chave) ?? [];
            const n = k + 1;
            return (
              <tbody key={l.chave} className={problemas.length > 0 ? 'com-problema' : undefined} data-camada={n}>
                <tr>
                  {soLer ? (
                    <>
                      <th scope="row">{l.unidade}</th>
                      <td className="n mono">{paraLer(l.topo)}</td>
                      <td className="n mono">{paraLer(l.base)}</td>
                      <td>{l.descricao || '—'}</td>
                      <td>{textoDaOrigem(l.origem, l.parametros)}</td>
                    </>
                  ) : (
                    <>
                      <th scope="row">
                        <label className="campo cptu-campo-da-tabela">
                          <span className="so-leitor">Camada {n}: unidade</span>
                          <input type="text" value={l.unidade} maxLength={80}
                            onChange={(e) => trocar(l.chave, { unidade: e.target.value })} />
                        </label>
                        <span className="cptu-origem">{textoDaOrigem(l.origem, l.parametros)}</span>
                      </th>
                      <td className="n">
                        <CampoNumerico rotulo={`Camada ${n}: topo (m)`} rotuloOculto value={l.topo} className="cptu-campo-da-tabela"
                          onChange={(e) => trocar(l.chave, { topo: e.target.value })} />
                      </td>
                      <td className="n">
                        <CampoNumerico rotulo={`Camada ${n}: base (m)`} rotuloOculto value={l.base} className="cptu-campo-da-tabela"
                          onChange={(e) => trocar(l.chave, { base: e.target.value })} />
                      </td>
                      <td>
                        <label className="campo cptu-campo-da-tabela cptu-campo-da-descricao">
                          <span className="so-leitor">Camada {n}: descrição</span>
                          <input type="text" value={l.descricao} maxLength={500}
                            onChange={(e) => trocar(l.chave, { descricao: e.target.value })} />
                        </label>
                      </td>
                      <td>
                        <div className="cptu-acoes-da-camada">
                          {k + 1 < ordem.length && (
                            <button type="button" className="ligacao" onClick={() => juntar(k)}>Juntar com a de baixo</button>
                          )}
                          <button type="button" className="ligacao" onClick={() => tirar(l.chave)}>Tirar</button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
                {problemas.length > 0 && (
                  <tr className="cptu-problema-da-camada">
                    <td colSpan={5}>
                      <span className="cptu-marca-texto">▲</span> {problemas.join('; ')}.
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </div>
      {!soLer && (
        <p className="cptu-nota">
          «Tirar» retira a camada na gravação: ela sai das vigentes e fica no histórico, com quem a tirou e quando.
        </p>
      )}
    </section>
  );
}

/** «2,35 ± 1,20» — a média e o desvio de uma grandeza, nas casas dela. */
const mediaEDesvio = (g: Grandeza, media: number, desvio: number) => `${textoDoValor(g, media)} ± ${textoDoValor(g, desvio)}`;

/**
 * A estatística por camada (spec §5.6): as leituras de cada camada [topo, base) e, para cada grandeza, média ±
 * desvio amostral, mínimo–máximo e CV — com a marca do R7 no cabeçalho e o porquê numerado embaixo, e «não se aplica»
 * onde a camada não tem leitura com valor.
 */
export function EstatisticaDasCamadas({ r, lidas, sobreRascunho = false }: {
  r: Resultado;
  lidas: readonly CamadaLida[];
  /** O resultado é o do rascunho da interpretação (não gravado): a tabela diz. */
  sobreRascunho?: boolean;
}) {
  if (lidas.length === 0) return null;
  const est = estatisticaPorCamada(r, lidas);
  const grandezas = GRANDEZAS_DA_ESTATISTICA.filter((g) => marcaDaColuna(g, r.premissas).mostrar);
  const marcas = grandezas.map((g) => marcaDaColuna(g, r.premissas));
  const motivos: string[] = [];
  for (const m of marcas) if (!m.homologada && m.motivo && !motivos.includes(m.motivo)) motivos.push(m.motivo);
  return (
    <section className="cartao cptu-estatistica-camadas" aria-label="Estatística por camada">
      <h3 className="cptu-titulo-da-secao">Estatística por camada</h3>
      <p className="cptu-nota">
        As leituras de cada camada, do topo até antes da base; média ± desvio amostral, mínimo–máximo e CV (desvio/|média|),
        sem as leituras que não têm valor.
      </p>
      {sobreRascunho && (
        <Aviso tipo="atencao" titulo="A estatística é sobre o rascunho (não gravado)">
          As premissas ou a edição que a tela mostra ainda não foram gravadas.
        </Aviso>
      )}
      <div className="rola">
        <table aria-label="Estatística por camada">
          <thead>
            <tr>
              <th scope="col">Camada</th>
              <th scope="col" className="n">Leituras</th>
              {grandezas.map((g, j) => {
                const m = marcas[j];
                const nota = !m.homologada && m.motivo ? motivos.indexOf(m.motivo) + 1 : null;
                return (
                  <th key={g} scope="col" className="n">
                    {GRANDEZAS[g].rotulo}{GRANDEZAS[g].unidade ? ` (${GRANDEZAS[g].unidade})` : ''}
                    {nota !== null && <span className="cptu-marca-texto"> ▲ ({nota})</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {est.map((e, i) => (
              <tr key={lidas[i].chave} data-camada={i + 1}>
                <th scope="row">
                  <span className="cptu-nome-da-camada">{lidas[i].unidade}</span>
                  <span className="cptu-faixa-da-camada">{faixa(e.topo, e.base)}</span>
                </th>
                <td className="n mono" data-leituras={e.leituras}>{numeroBr(e.leituras, 0)}</td>
                {grandezas.map((g) => {
                  const s = e.estatistica[g];
                  if (!s || s.n === 0) {
                    return <td key={g} className="n" data-grandeza={g}><span className="cptu-nota">não se aplica</span></td>;
                  }
                  return (
                    <td key={g} className="n mono cptu-celula-estatistica" data-grandeza={g} data-n={s.n} data-media={s.media}>
                      <span>{mediaEDesvio(g, s.media, s.desvio)}</span>
                      <span>{textoDoValor(g, s.minimo)}–{textoDoValor(g, s.maximo)}</span>
                      <span>CV {Number.isFinite(s.cv) ? `${numeroBr(s.cv * 100, 1)} %` : '—'}</span>
                      {s.n !== e.leituras && <span className="cptu-nota">em {numeroBr(s.n, 0)}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NotasDoPorque motivos={motivos} />
    </section>
  );
}
